import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../api/client';
import { Button, Card, Select, Alert, PageHeader, PromptTextarea } from '../components/ui';
import { Header } from '../components/Header';
import { CampaignCard } from '../components/CampaignCard';
import { Landing } from './Landing';

interface Campaign {
  id: string;
  title: string;
  category: string;
  status: string;
  created_at: string;
  extractions_remaining: number;
}

interface AdventureTemplate {
  id: string;
  title: string;
  category: string;
  difficulty: string;
  duration_hours: number;
  tags: string[];
}

export function CampaignList() {
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const api = useApiClient();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [category, setCategory] = useState('dnd');
  const [language, setLanguage] = useState('english');
  const [prompt, setPrompt] = useState('');
  const [creating, setCreating] = useState(false);
  const [adventureMode, setAdventureMode] = useState<'random' | 'template'>('random');
  const [templates, setTemplates] = useState<AdventureTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/sign-in');
      return;
    }
    if (isLoaded && isSignedIn) {
      loadCampaigns();
    }
  }, [isLoaded, isSignedIn, navigate]);

  async function loadCampaigns() {
    try {
      const data = (await api.get('/campaigns')) as Campaign[];
      setCampaigns(data || []);
      setError('');
    } catch (err) {
      setError(`Failed to load campaigns: ${err}`);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadTemplates(selectedCategory: string) {
    setLoadingTemplates(true);
    try {
      const data = (await api.get(`/adventure-templates?category=${selectedCategory}`)) as AdventureTemplate[];
      setTemplates(data || []);
      setSelectedTemplate('');
    } catch (err) {
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }

  async function handleCreate() {
    if (!category) {
      setError('Please select a category');
      return;
    }
    if (adventureMode === 'template' && !selectedTemplate) {
      setError('Please select a template');
      return;
    }
    setCreating(true);
    try {
      const result = (await api.post('/campaigns', {
        category,
        language,
        prompt: prompt || undefined,
        template_id: adventureMode === 'template' ? selectedTemplate : undefined,
      })) as Campaign;
      // Generate initial turn for opening narration
      try {
        await api.post(`/campaigns/${result.id}/initial-turn`, {});
      } catch (err) {
        // Non-blocking: if initial turn fails, still navigate (user can manually play first turn)
        console.warn('Failed to generate initial turn:', err);
      }
      navigate(`/campaigns/${result.id}`);
    } catch (err) {
      setError(`Failed to create campaign: ${err}`);
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await api.delete(`/campaigns/${id}`);
      setCampaigns(campaigns.filter(c => c.id !== id));
    } catch (err) {
      setError(`Failed to delete campaign: ${err}`);
    }
  }

  async function handleExtract(id: string) {
    try {
      const result = (await api.post(`/campaigns/${id}/extract`, {})) as { narrative: string };
      // Create download link for markdown
      const element = document.createElement('a');
      element.setAttribute('href', `data:text/markdown;charset=utf-8,${encodeURIComponent(result.narrative)}`);
      const campaign = campaigns.find(c => c.id === id);
      element.setAttribute('download', `${campaign?.title || 'campaign'}-extraction.md`);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      // Reload campaigns to update extraction counter
      loadCampaigns();
    } catch (err) {
      setError(`Failed to extract campaign: ${err}`);
    }
  }

  if (!isLoaded || !isSignedIn) {
    return <div style={{ padding: 'var(--space-4)' }}>Loading...</div>;
  }

  // Show landing page when no campaigns exist
  if (campaigns.length === 0 && !showCreate && !loading) {
    return (
      <>
        <Header />
        <Landing onCreateCampaign={() => setShowCreate(true)} />
      </>
    );
  }

  return (
    <>
      <Header />
      <div style={{ padding: 'var(--space-4)', maxWidth: '1000px', margin: '0 auto' }}>
      <PageHeader title="Campaigns" />

      {error && <Alert variant="error">{error}</Alert>}

      {loading ? (
        <div>Loading campaigns...</div>
      ) : campaigns.length === 0 && !showCreate ? (
        <Card>
          <p>No campaigns yet. Create one to get started!</p>
          <Button onClick={() => setShowCreate(true)}>Create Campaign</Button>
        </Card>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
            {campaigns.length < 3 && !showCreate && (
              <Button onClick={() => setShowCreate(true)}>Create Campaign</Button>
            )}
            {campaigns.length >= 3 && !showCreate && (
              <Button disabled>Campaign Limit Reached (3/3)</Button>
            )}
          </div>

          {showCreate && (
            <Card>
              <h3 style={{ marginTop: 0 }}>Create New Campaign</h3>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontWeight: 500 }}>
                  Language
                </label>
                <Select
                  value={language}
                  onChange={e => setLanguage(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="english">English</option>
                  <option value="espanol">Español</option>
                  <option value="italiano">Italiano</option>
                  <option value="francais">Français</option>
                  <option value="deutsch">Deutsch</option>
                  <option value="portuguese">Português</option>
                </Select>
              </div>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontWeight: 500 }}>
                  Genre
                </label>
                <Select
                  value={category}
                  onChange={e => {
                    setCategory(e.target.value);
                    if (adventureMode === 'template') {
                      loadTemplates(e.target.value);
                    }
                  }}
                  style={{ width: '100%' }}
                >
                  <option value="dnd">D&D Fantasy</option>
                  <option value="investigation">Investigation</option>
                  <option value="sci-fi">Sci-Fi</option>
                  <option value="horror">Horror</option>
                </Select>
              </div>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <p style={{ margin: 0, marginBottom: 'var(--space-2)', fontWeight: 500 }}>Adventure:</p>
                <div style={{ marginLeft: 'var(--space-3)' }}>
                  <div style={{ marginBottom: 'var(--space-2)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <input
                        type="radio"
                        name="adventureMode"
                        value="random"
                        checked={adventureMode === 'random'}
                        onChange={e => setAdventureMode('random')}
                      />
                      Random (LLM Generated)
                    </label>
                  </div>
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <input
                        type="radio"
                        name="adventureMode"
                        value="template"
                        checked={adventureMode === 'template'}
                        onChange={e => {
                          setAdventureMode('template');
                          loadTemplates(category);
                        }}
                      />
                      Choose Template
                    </label>
                  </div>
                  {adventureMode === 'template' && (
                    <div style={{ marginLeft: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
                      <Select
                        value={selectedTemplate}
                        onChange={e => setSelectedTemplate(e.target.value)}
                        disabled={loadingTemplates}
                      >
                        <option value="">Select a template...</option>
                        {templates.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.title} ({t.difficulty}, {t.duration_hours}h)
                          </option>
                        ))}
                      </Select>
                    </div>
                  )}
                </div>
              </div>
              {adventureMode === 'random' && (
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontWeight: 500 }}>
                    Additional Prompt (optional)
                  </label>
                  <PromptTextarea
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                    placeholder="e.g., cyberpunk setting with dragons..."
                  />
                </div>
              )}
              <div className="form-actions-row" style={{ display: 'flex', gap: 'var(--space-3)' }}>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  loading={creating}
                >
                  {creating ? 'Creating...' : 'Create'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowCreate(false);
                    setAdventureMode('random');
                    setSelectedTemplate('');
                    setPrompt('');
                    setLanguage('english');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Card>
          )}

          {campaigns.length > 0 && (
            <div style={{ marginTop: 'var(--space-6)' }}>
              <h2>Your Campaigns ({campaigns.length}/3)</h2>
              {campaigns.map(c => (
                <CampaignCard
                  key={c.id}
                  campaign={c}
                  onExport={handleExtract}
                  onResume={id => navigate(`/campaigns/${id}`)}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </>
      )}
      </div>
    </>
  );
}
